import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/useT";
import PageBlockRenderer from "@/components/PageBlockRenderer";
import SeoHead from "@/components/SeoHead";
import { useBusiness } from "@/context/BusinessContext";

/** Renders a CMS page (about / privacy / terms / location/<slug>). */
export default function CMSPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params["*"] || params.slug || "about";
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const t = useT();
  const { business, loading: bizLoading } = useBusiness();

  useEffect(() => {
    // Wait for the tenant context to resolve (handles ?biz=<slug> deep links).
    if (bizLoading) return;
    setPage(null);
    setError(null);
    api
      .get(`/pages/${slug}`)
      .then(({ data }) => {
        setPage(data);
        if (data.seo_title) document.title = data.seo_title;
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Page not found"));
  }, [slug, bizLoading]);

  // Build extra JSON-LD for location pages: FAQPage + BreadcrumbList.
  const extras = useMemo(() => {
    if (!page) return [];
    const out = [];
    const isLocation = (page.page_type === "location") || slug.startsWith("locations/");
    if (isLocation) {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      out.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: origin || "/" },
          { "@type": "ListItem", position: 2, name: "Locations", item: `${origin}/locations` },
          { "@type": "ListItem", position: 3, name: page.title, item: `${origin}/${slug}` },
        ],
      });
      // Q&A blocks → FAQPage. We support both shapes:
      //   { kind: "faq", heading: "Q", content: "A" }  (PageBlock-validated)
      //   { kind: "faq", settings: { question, answer } } (also accepted)
      const faqBlocks = Array.isArray(page.blocks)
        ? page.blocks
            .filter((b) => b && b.kind === "faq")
            .map((b) => {
              const q = b.settings?.question || b.heading || b.question || "";
              const a = b.settings?.answer || b.content || b.answer || "";
              return q && a ? { q, a } : null;
            })
            .filter(Boolean)
        : [];
      if (faqBlocks.length > 0) {
        out.push({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqBlocks.map((b) => ({
            "@type": "Question",
            name: b.q,
            acceptedAnswer: { "@type": "Answer", text: b.a },
          })),
        });
      }
      if (business?.suburb) {
        out.push({
          "@context": "https://schema.org",
          "@type": "Place",
          name: page.title || `${business.business_name} — ${business.suburb}`,
          address: {
            "@type": "PostalAddress",
            streetAddress: [business.address1, business.address2].filter(Boolean).join(" "),
            addressLocality: business.suburb,
            addressRegion: business.state,
            postalCode: business.postcode,
            addressCountry: "AU",
          },
        });
      }
    }
    return out;
  }, [page, slug, business]);

  if (error) {
    return (
      <div data-testid="cms-page-error" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="font-serif-display text-3xl">Page not found</p>
        <p className="mt-2 text-sm text-[#5C625E]">{error}</p>
      </div>
    );
  }
  if (!page) {
    return <div className="max-w-3xl mx-auto px-6 py-20 text-[#5C625E]">Loading…</div>;
  }

  const blocks = Array.isArray(page.blocks) ? page.blocks : [];
  const hasBlocks = blocks.length > 0;

  return (
    <article
      data-testid="cms-page"
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-16"
    >
      <SeoHead
        scope="page"
        title={page.seo_title || page.title}
        description={page.seo_description || ""}
        extras={extras}
      />
      <h1 className="font-serif-display text-4xl sm:text-5xl">{t(page, "title")}</h1>
      {/* Always render body_md when present. When the page also carries
          structured blocks (e.g. FAQ accordions for AEO), they appear
          BELOW the prose so the narrative reads first and the Q&A acts as
          a "more info" footer that AI engines still pick up via JSON-LD. */}
      {t(page, "body_md") && (
        <div
          data-testid="cms-page-body"
          className="prose prose-neutral max-w-none mt-8 whitespace-pre-line leading-relaxed text-[#3a3c3a]"
          dangerouslySetInnerHTML={{ __html: renderLightMarkdown(t(page, "body_md")) }}
        />
      )}
      {hasBlocks && (
        <div className="mt-10">
          <PageBlockRenderer blocks={blocks} />
        </div>
      )}
    </article>
  );
}

function renderLightMarkdown(src = "") {
  // Tiny markdown subset: headings (## ###), bold (**), and paragraphs.
  // Anything more advanced should be added when there's a real CMS editor.
  const safe = src
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return safe
    .replace(/^###\s+(.*)$/gm, '<h3 class="font-serif-display text-2xl mt-8">$1</h3>')
    .replace(/^##\s+(.*)$/gm, '<h2 class="font-serif-display text-3xl mt-10">$1</h2>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .split(/\n{2,}/)
    .map((p) => (p.startsWith("<h") ? p : `<p class="mt-4">${p.replace(/\n/g, "<br/>")}</p>`))
    .join("");
}
